/**
 * threadTurnActivityRepository — "somebody is asking something in this thread,
 * right now", live on every device and for every participant.
 *
 * Owner report 2026-09-17: Miles asked something on his phone, opened his
 * laptop, and the spinning widget was dark. It had only ever had ONE
 * cross-device source — a 45-second poll of `prest0nListActiveTasks`
 * (useGlobalChat's ACTIVE_TASK_DISCOVERY_POLL_MS) — because the task document
 * a browser would otherwise watch is unreadable by clients: there is no
 * firestore.rules match for `accounts/{id}/tasks` at all. So a second device
 * was blind for up to three quarters of a minute, which is longer than most
 * turns take, and a teammate in a shared thread saw the question land and then
 * nothing at all until the answer arrived.
 *
 * WHAT WE WATCH INSTEAD: the thread's own SESSION document. It was already
 * server-write-only and member-readable, and its id is derivable from the
 * thread with no lookup (`sess_thr_<threadId>` — threadInvariant.ts on the
 * server, and the thread index doc already stores it), so a single-document
 * listener is enough. The server writes `activeTurn` there when a turn is
 * accepted and clears it at the one terminal door every lane passes through
 * (sessionManager.markThreadTurnActive / clearThreadTurnActive). Same posture
 * as `accounts/{id}/ingestJobs`, which members read so the chat can subscribe
 * to extraction progress.
 *
 * ONE LISTENER, THE OPEN THREAD ONLY (the scope Miles set). Attached and
 * detached with the active thread, exactly like the receipts and reactions
 * listeners next to it, with the same capped retry ladder — an onSnapshot
 * error is terminal for that listener, so a thread must never be left deaf for
 * the rest of the session.
 *
 * NOT a replacement for the poll. The poll stays exactly as it was: it is the
 * liveness evidence a long turn needs (it refreshes the per-thread heartbeat
 * the stuck-flag sweep reads) and the fallback for a participant this document
 * is unreachable for. This listener only makes the signal instant.
 */
import { onSnapshot } from 'firebase/firestore';
import { db, doc } from './firestoreUtils';
import { GROUP_LISTENER_RETRY_DELAYS_MS } from './groupThreadMessagesRepository';

/** The thread's live turn, as the server publishes it. Null ⇒ nothing running. */
export interface ThreadTurnActivity {
  taskId: string;
  /** Who set it going. The indicator names this person; never their role. */
  startedByUid: string;
  /** When the work began, so every device shows the same elapsed clock. */
  startedAtMs: number;
}

/**
 * The thread's session id. Mirrors the server's `sess_thr_<threadId>`
 * (functions/src/prest0nSession/threadInvariant.ts) — parity-pinned, because a
 * drift here makes the listener watch a document that will never exist and the
 * indicator silently goes back to being 45 seconds late.
 */
export function threadSessionId(threadId: string): string {
  return `sess_thr_${threadId}`;
}

/** Parse the stored shape, refusing anything that is not a usable signal. */
export function parseThreadTurnActivity(raw: unknown): ThreadTurnActivity | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const taskId = typeof rec.taskId === 'string' ? rec.taskId.trim() : '';
  if (!taskId) return null;
  const startedAtMs = typeof rec.startedAtMs === 'number' && Number.isFinite(rec.startedAtMs) && rec.startedAtMs > 0
    ? rec.startedAtMs
    : 0;
  return {
    taskId,
    startedByUid: typeof rec.startedByUid === 'string' ? rec.startedByUid.trim() : '',
    startedAtMs,
  };
}

/**
 * Watch one thread's live turn. `onActivity(null)` means nothing is running.
 *
 * `accountId` is the workspace the THREAD belongs to — for a shared thread
 * that is the org every participant is a member of, which is what makes the
 * document readable for all of them.
 */
export function subscribeThreadTurnActivity(
  accountId: string,
  threadId: string,
  onActivity: (activity: ThreadTurnActivity | null) => void,
): () => void {
  const account = (accountId ?? '').trim();
  const id = (threadId ?? '').trim();
  if (!account || !id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = GROUP_LISTENER_RETRY_DELAYS_MS[
      Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)
    ];
    retryIndex += 1;
    retryTimer = setTimeout(attach, delay);
  };

  const attach = (): void => {
    if (stopped) return;
    try {
      const unsubscribe = onSnapshot(
        doc(db, 'accounts', account, 'sessions', threadSessionId(id)),
        (snap) => {
          // Only a server-confirmed snapshot resets the backoff: persistence
          // replays a cache snapshot on every reattach even when the watch
          // stream is about to error again, and a cache-driven reset would pin
          // the ladder at its shortest delay in a hot loop (the idiom in
          // chatThreadMessagesRepository).
          if (!snap.metadata.fromCache) retryIndex = 0;
          const data = snap.exists() ? snap.data() : null;
          onActivity(parseThreadTurnActivity(data?.activeTurn));
        },
        () => {
          // A thread whose session document this viewer cannot read (a
          // participant whose membership is still settling, a workspace they
          // are not a member of) lands here. Retry on the ladder and let the
          // 45-second poll carry the signal meanwhile — never surface an error
          // for a courtesy channel.
          try { unsubscribe(); } catch { /* already detached */ }
          detachInner = null;
          scheduleRetry();
        },
      );
      detachInner = unsubscribe;
    } catch {
      scheduleRetry();
    }
  };

  attach();

  return () => {
    stopped = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    try { detachInner?.(); } catch { /* already detached */ }
    detachInner = null;
  };
}
